import { cx } from './cx'
import s from './ProgressRing.module.css'

export type RingTone = 'brand' | 'good' | 'warn' | 'critical' | 'neutral'

/** The centre value's type step, chosen by the room INSIDE the ring. */
export type RingScale = 'lg' | 'md' | 'sm' | 'xs'

/**
 * The type step the centre value can take inside a ring whose inner diameter
 * is `inner` px (#2406, design-sync kit finding "ProgressRing").
 *
 * The value was a fixed `--fs-lg` at every size, so "100%" in a 48px ring ran
 * over the arc. `100%` sets roughly 2.6 em wide, and it sits on the ring's
 * centre line where the free chord IS the inner diameter, so each step is the
 * smallest inner diameter that holds four tabular glyphs of that step with a
 * little air: lg 17px needs ~44px of chord -> 60, md 14 -> 44, sm 12.5 -> 34,
 * and below that the ramp's floor, `--fs-xs`. Steps, not a computed px size:
 * the vocabulary is closed, so a ring picks a token rather than inventing one.
 */
export function ringScale(inner: number): RingScale {
  if (inner >= 60) return 'lg'
  if (inner >= 44) return 'md'
  if (inner >= 34) return 'sm'
  return 'xs'
}

/** Below this inner diameter a caption has no second line to sit on. */
const CAPTION_MIN_INNER = 56

/**
 * A circular progress ring (the Kendo CircularProgressBar of the kit) — one
 * SVG donut with an animated stroke + a slot for a centred value/label. Used by
 * the Setup Cockpit's "how ready is this instance" gauge and anywhere a single
 * 0–100 completion reads better as a dial than a bar. Pure/controlled: pass
 * `value` (0–100); style the arc with `tone`; put whatever you like in the middle
 * via `children` (defaults to `NN%`).
 *
 * Everything in the middle is bounded by the INNER diameter: the value steps
 * down the type ramp as the ring shrinks (`ringScale`), and the caption is
 * clipped to the chord it sits on and dropped when there is no room for a
 * second line — it is always in the ring's accessible name, which is the whole
 * reading for a screen reader since the ring is one `img`.
 */
export function ProgressRing({
  value, size = 96, thickness = 8, tone = 'brand', trackless = false, className, label, children,
}: {
  value: number
  size?: number
  thickness?: number
  tone?: RingTone
  trackless?: boolean
  className?: string
  /** small caption under the centred value */
  label?: string
  /** override the centre content (defaults to the rounded percentage) */
  children?: React.ReactNode
}) {
  const v = Math.min(100, Math.max(0, value))
  const r = (size - thickness) / 2
  const c = 2 * Math.PI * r
  const dash = (v / 100) * c
  const inner = Math.max(0, size - 2 * thickness)
  const showCaption = !!label && inner >= CAPTION_MIN_INNER
  return (
    <div className={cx(s.ring, className)} role="img" data-scale={ringScale(inner)}
      style={{
        width: size, height: size,
        // The room inside the arc, read by the centre slot and its caption.
        ['--ring-inner' as string]: `${inner}px`,
      }}
      aria-label={label ? `${label}: ${Math.round(v)}%` : `${Math.round(v)} percent`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={s.svg}>
        {!trackless && (
          <circle className={s.track} cx={size / 2} cy={size / 2} r={r} strokeWidth={thickness} fill="none" />
        )}
        {/* No cap at 0%. A zero-length dash with a ROUND cap is still drawn —
            as a dot at twelve o'clock that reads as "a little progress" when
            there is none. A butt cap on a zero-length dash draws nothing, and
            the element stays so the dash still animates up from zero. */}
        <circle
          className={cx(s.arc, s[tone])} cx={size / 2} cy={size / 2} r={r} strokeWidth={thickness} fill="none"
          strokeDasharray={`${dash} ${c}`} strokeLinecap={dash > 0 ? 'round' : 'butt'}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          data-testid="progress-ring-arc"
        />
      </svg>
      <div className={s.center}>
        <span className={s.value}>{children ?? `${Math.round(v)}%`}</span>
        {showCaption && <span className={s.label} title={label}>{label}</span>}
      </div>
    </div>
  )
}
